import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import type {
  SwitchCarrierOrderRequest,
  SwitchCarrierOrderResponse,
  SwitchCarrierProvider,
} from '@/infrastructure/FulfillmentAdminClient';
import {
  clearCarrierSwitchDraft,
  describeSwitchFailure,
  providerOf,
  readCarrierSwitchDraft,
  saveCarrierSwitchDraft,
  SWITCH_CARRIER_PROVIDERS,
} from './carrierSwitch';

/** The carrier order the order holds now, as the page read it when the dialog opened. */
export interface SwitchCarrierSource {
  carrier: string;
  carrierOrderId: string;
}

interface SwitchCarrierOrderModalProps {
  readonly show: boolean;
  readonly orderId: number;
  /** Null when the dialog resumes a switch this browser kept; the kept switch names its own source. */
  readonly source: SwitchCarrierSource | null;
  readonly onHide: () => void;
  /** Resolves with the server's answer; a rejection stays in the dialog, worded for what to do next. */
  readonly onSubmit: (request: SwitchCarrierOrderRequest) => Promise<SwitchCarrierOrderResponse>;
}

const REASON_MAX_LENGTH = 500;

const TITLE_ID = 'switch-carrier-title';

const STORAGE_REFUSED =
  'This browser cannot keep the switch key, so nothing was sent. Allow site storage and try again.';

/**
 * Switches an order's carrier in Fulfillment 2.0 (SSD-7064), the 2.0 copy of OMS Center's Switch Shipping
 * Provider. The key is saved before the request leaves and kept until the switch finishes, so a dialog
 * reopened after an uncertain answer resumes the same switch instead of cancelling or booking twice.
 */
export function SwitchCarrierOrderModal({ show, orderId, source, onHide, onSubmit }: SwitchCarrierOrderModalProps) {
  const [shownSource, setShownSource] = useState<SwitchCarrierSource | null>(null);
  const [targetProvider, setTargetProvider] = useState<SwitchCarrierProvider | ''>('');
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [inputsLocked, setInputsLocked] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [pastCancel, setPastCancel] = useState(false);
  const [retired, setRetired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** setSubmitting(true) only shuts the button on the next render, a tick too late for a double click. */
  const inFlightRef = useRef(false);

  // Opening the dialog resumes the switch this browser kept, or starts a new one against the source shown.
  useEffect(() => {
    if (!show) {
      return;
    }
    const kept = readCarrierSwitchDraft(orderId);
    setShownSource(
      kept ? { carrier: kept.sourceCarrier, carrierOrderId: kept.request.expectedCarrierOrderId } : source,
    );
    setTargetProvider(kept?.request.targetProvider ?? '');
    setReason(kept?.request.reason ?? '');
    setConfirmation('');
    setIdempotencyKey(kept?.request.idempotencyKey ?? uuidv4());
    setInputsLocked(Boolean(kept));
    setResuming(Boolean(kept));
    setPastCancel(kept?.pastCancel ?? false);
    setRetired(false);
    setError(null);
    // Only opening the dialog resets it; a reload of the order underneath must not wipe what was typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderId]);

  const sourceProvider = providerOf(shownSource?.carrier);
  const targets = SWITCH_CARRIER_PROVIDERS.filter((provider) => provider.value !== sourceProvider);
  const targetLabel = SWITCH_CARRIER_PROVIDERS.find((provider) => provider.value === targetProvider)?.label ?? '';

  const describeBlock = (): string => {
    if (retired) {
      return 'This switch has ended; close the dialog';
    }
    if (!shownSource) {
      return 'This order holds no carrier order to switch';
    }
    if (!targetProvider) {
      return 'Pick the carrier to switch to';
    }
    if (targetProvider === sourceProvider) {
      return 'Pick a carrier other than the current one';
    }
    if (!reason.trim()) {
      return 'Enter a reason';
    }
    return confirmation.trim() === String(orderId) ? '' : `Type ${orderId} to confirm`;
  };
  const block = describeBlock();

  const submit = async () => {
    if (block !== '' || inFlightRef.current || !shownSource || !targetProvider) {
      return;
    }
    const request: SwitchCarrierOrderRequest = {
      targetProvider,
      expectedCarrierOrderId: shownSource.carrierOrderId,
      idempotencyKey,
      reason: reason.trim(),
    };
    try {
      saveCarrierSwitchDraft(orderId, { request, sourceCarrier: shownSource.carrier, pastCancel: true });
    } catch {
      setError(STORAGE_REFUSED);
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    setInputsLocked(true);
    setError(null);
    try {
      await onSubmit(request);
      clearCarrierSwitchDraft(orderId);
      onHide();
    } catch (err) {
      const refusal = describeSwitchFailure(err, {
        target: targetLabel,
        sourceCarrier: shownSource.carrier,
        sourceCarrierOrderId: shownSource.carrierOrderId,
        pastCancel,
      });
      if (refusal.fate === 'keep') {
        setPastCancel(true);
        setResuming(true);
      } else {
        clearCarrierSwitchDraft(orderId);
        setResuming(false);
        if (refusal.fate === 'renew') {
          setIdempotencyKey(uuidv4());
          setInputsLocked(false);
        } else {
          setRetired(true);
        }
      }
      setError(refusal.message);
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={close} backdrop={submitting ? 'static' : true} aria-labelledby={TITLE_ID}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title as="h5" id={TITLE_ID}>
          Switch Shipping Provider #{orderId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger">
          The target carrier is checked before anything is cancelled, and it is booked only after the current carrier
          confirms the cancel. If the outcome is uncertain, send again here — never start a new switch.
          <div data-testid="switch-source">
            Source: {shownSource ? `${shownSource.carrier} (${shownSource.carrierOrderId})` : '—'}
          </div>
        </Alert>
        {resuming && !retired && (
          <Alert variant="info" data-testid="switch-resume">
            Resuming the switch to {targetLabel} sent earlier, with the same key. Sending again finishes it without
            cancelling twice.
          </Alert>
        )}
        {error && (
          <Alert variant="danger" data-testid="switch-error">
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3" controlId="switch-carrier-target">
          <Form.Label>Target Provider *</Form.Label>
          <Form.Select
            value={targetProvider}
            disabled={submitting || inputsLocked || retired}
            onChange={(event) => setTargetProvider(event.target.value as SwitchCarrierProvider | '')}
          >
            <option value="">Select a target provider</option>
            {targets.map((provider) => (
              <option key={provider.value} value={provider.value}>
                {provider.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3" controlId="switch-carrier-reason">
          <Form.Label>Reason *</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={REASON_MAX_LENGTH}
            value={reason}
            disabled={submitting || inputsLocked || retired}
            placeholder="Explain why the provider must be changed"
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="switch-carrier-confirmation">
          <Form.Label>
            Type <strong>{orderId}</strong> to confirm *
          </Form.Label>
          <Form.Control
            value={confirmation}
            autoComplete="off"
            disabled={submitting || retired}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="blocked-note">{submitting ? '' : block}</span>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-line btn-sm" disabled={submitting} onClick={close}>
            {retired ? 'Close' : 'Cancel'}
          </button>
          <Button
            variant="danger"
            size="sm"
            disabled={submitting || block !== ''}
            title={block}
            onClick={() => void submit()}
          >
            {submitting ? 'Switching carrier…' : resuming ? 'Resume Provider Switch' : 'Confirm Provider Switch'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
